/**
 * Profile Payload Builder
 *
 * SECURITY: Single gatekeeper for outbound profile data.
 * Every path that sends profileData must use this function.
 * If a field is not in sharedFields, it does not leave the device.
 */

// aboutMe is a bundle key in sharedFields — these are the actual data fields it expands to.
// currentCity (hometown) is intentionally NOT here: it's its own auto-selected shareable
// field (pre-checked, shared only on confirm), carried directly when 'currentCity' is in
// sharedFields (see profile-hometown-sharing).
// Exported as the single source of truth for the bundle: the D2 live-sharing leak guard
// (connectionName.js) clears exactly these when it re-adds the 'aboutMe' flag.
export const ABOUT_ME_FIELDS = ['role', 'organization', 'whatIDo', 'placesLived'];

/**
 * Does this profile have anything in the About-me bundle?
 *
 * Lives here because this module owns what the bundle IS. Three hand-rolled
 * copies of this test existed (SharingSelectionOverlay twice, InlineSharingList
 * once) and the Full-profile share path had none at all — which is how
 * "Full profile" shipped without About-me: nothing there ever asked the
 * question. (2026-08-20)
 *
 * @param {Object} profile - a full own-profile object
 * @returns {boolean}
 */
export const hasAboutMeContent = (profile) =>
  !!profile && ABOUT_ME_FIELDS.some((f) => !!profile[f]);

/**
 * Build a send-safe profile payload filtered by sharedFields.
 *
 * @param {Object} fullProfile - The complete profile object (from getProfileData / profileRef)
 * @param {string[]} sharedFields - Fields the user chose to share (e.g. ['photoUrl', 'phone'])
 * @param {Object} [overrides] - Fields to set regardless of sharedFields (name, nameSharing, profileUserId)
 * @returns {Object} Filtered profile payload safe to send over the wire
 */
export const buildProfilePayload = (fullProfile, sharedFields, overrides = {}) => {
  const payload = {};

  // Copy only fields listed in sharedFields
  for (const field of sharedFields) {
    if (field === 'aboutMe') continue; // Bundle key — expanded below
    if (fullProfile[field] !== undefined) {
      payload[field] = fullProfile[field];
    }
  }

  // Expand aboutMe bundle to individual data fields
  if (sharedFields.includes('aboutMe')) {
    for (const field of ABOUT_ME_FIELDS) {
      if (fullProfile[field] !== undefined) {
        payload[field] = fullProfile[field];
      }
    }
  }

  // Photo meta (face box, focal point) is part of the photo, not a
  // separate field: it travels exactly when the photo does (signup-foundations).
  if (sharedFields.includes('photoUrl') && fullProfile.photoMeta !== undefined) {
    payload.photoMeta = fullProfile.photoMeta;
  }

  // Where the hometown is (its picked point, location is a primitive) is part
  // of the hometown: it travels exactly when the hometown does.
  if (sharedFields.includes('currentCity') && fullProfile.currentCityPlace !== undefined) {
    payload.currentCityPlace = fullProfile.currentCityPlace;
  }

  // The person's chosen colour (signup A′, Drop 7) is shown to everyone who
  // holds them, like the first name and photo — never gated on a share flag.
  // Absent until chosen (unset = purple), so a no-colour payload is unchanged.
  if (fullProfile.colour !== undefined) {
    payload.colour = fullProfile.colour;
  }

  // Structural metadata — always included
  payload.sharedFields = sharedFields;

  // Apply overrides (name, nameSharing, profileUserId, etc.)
  return { ...payload, ...overrides };
};
